import { z } from "zod";
import { wineTypeSchema } from "./wine.js";
/**
 * A wine area described by the model — the fourth question. Decided
 * 2026-09-28 in `project-scope.md`; the shape was worked out in
 * `discover-wines.md` and previewed on Champagne before anything was built.
 *
 * **A report belongs to the system, not to a cellar**: a description of Barolo
 * is not a fact of anybody's cellar, so nothing here carries a cellar id, and
 * the routes stand beside `/api/cellars` rather than inside it.
 */
/** Where a region is, roughly — its centre or its main village, never a
 *  border. See the decision for why a border is never asked for. */
export const areaPositionSchema = z.object({
    lat: z.number(),
    lng: z.number(),
});
/**
 * How a producer stands in its region, in three words the page draws as
 * *Referens*, *Etablerad* and *Värd att upptäcka*. The model's claim on one
 * day, like everything else in a report.
 */
export const AREA_PRODUCER_TIERS = ["reference", "established", "discovery"];
export const areaProducerTierSchema = z.enum(AREA_PRODUCER_TIERS);
export const areaProducerSchema = z.object({
    name: z.string(),
    why: z.string(),
    tier: areaProducerTierSchema,
    /**
     * The house's best-known wines, as the label prints them — *Dom Pérignon*
     * under *Moët & Chandon*, *Cristal* under *Louis Roederer*. Added the same
     * evening, when the owner looked for Dom Pérignon and found no producer by
     * that name: a cuvée can be better known than the house that makes it, and
     * a cellar may have registered it as its producer.
     *
     * **Defaulted to none, and permanently so**: a report stored before the
     * field existed has no cuvées and never will, unless it is run again.
     */
    cuvees: z.array(z.string()).default([]),
});
export const areaRegionSchema = z.object({
    name: z.string(),
    /** *Delområde*, *Kommun*, *Appellation* — the level it sits at, in words. */
    level: z.string(),
    description: z.string(),
    grapes: z.array(z.string()),
    position: areaPositionSchema.nullable(),
    producers: z.array(areaProducerSchema),
});
export const areaStepSchema = z.object({
    name: z.string(),
    description: z.string(),
});
/** A choice that divides the producers, with the ones from this same report
 *  that make it. An example the report does not name elsewhere is dropped
 *  before it is stored — see `area/describe.ts`. */
export const areaChoiceSchema = z.object({
    name: z.string(),
    description: z.string(),
    examples: z.array(z.string()),
});
/**
 * One year of the vintage table: Systembolaget's shape, a grade from 1 to 5.
 * **Null is not a low grade** — it is a year that could not be graded: newer
 * than the model knows, too young to judge, or one the area declared no
 * vintage wine in. The remark says which.
 */
export const areaVintageSchema = z.object({
    year: z.number().int(),
    grade: z.number().int().min(1).max(5).nullable(),
    remark: z.string().nullable(),
});
/** The report itself, as it is stored and as the page draws it. */
export const areaReportContentSchema = z.object({
    /** Paragraphs, separated by a blank line. */
    summary: z.string(),
    classification: z.string().nullable(),
    keyGrapes: z.array(z.string()),
    /** Where the map centres. Null when the model gave nothing usable. */
    center: areaPositionSchema.nullable(),
    production: z.object({
        summary: z.string(),
        steps: z.array(areaStepSchema),
        choices: z.array(areaChoiceSchema),
    }),
    regions: z.array(areaRegionSchema),
    /** Newest year first. */
    vintages: z.array(areaVintageSchema),
    /** What the model said it was unsure of, one sentence each. */
    caveats: z.array(z.string()),
});
/**
 * Where a report stands.
 *
 * `running` while the model is being asked; `draft` once it has answered and
 * until it is published; `published` for the one report per kind, country and
 * area that readers see. A draft that was once published says so in
 * `wasPublished`, so a worse rerun can be taken back by publishing it again.
 */
export const areaReportStatusSchema = z.enum(["running", "draft", "published"]);
/** One run, as the administrator's list shows it. */
/**
 * A fix an earlier fact check of the same area established, carried into a
 * new run of it — task 10.46. The part it was about, what was wrong, the fact
 * that is right, and where that was found.
 */
export const areaCorrectionSchema = z.object({
    part: z.string(),
    problem: z.string(),
    correction: z.string(),
    source: z.object({ title: z.string(), url: z.string() }).nullable(),
});
export const areaReportSummarySchema = z.object({
    id: z.string(),
    kind: wineTypeSchema,
    countryCode: z.string(),
    area: z.string(),
    status: areaReportStatusSchema,
    wasPublished: z.boolean(),
    runAt: z.string(),
    runBy: z.string().nullable(),
    model: z.string().nullable(),
    publishedAt: z.string().nullable(),
    /** How many corrections from earlier fact checks the run was asked with —
     *  task 10.46; nought for everybody but the administrator. Required since
     *  2026-09-29, once revision 70 was serving it. */
    correctionCount: z.number().int(),
    /** When the run was last fact-checked — task 10.48; null when it has not
     *  been, and for everybody but the administrator. Required since
     *  2026-09-29, once revision 71 was serving it. */
    reviewedAt: z.string().nullable(),
});
/** Every run there has been, and whether the administrator may run another —
 *  false when their switch is off or there is no key. */
export const areaReportAdminListResponseSchema = z.object({
    canRun: z.boolean(),
    reports: z.array(areaReportSummarySchema),
});
/** One published report, as the readers' tree lists it. */
export const publishedAreaReportSchema = z.object({
    id: z.string(),
    kind: wineTypeSchema,
    countryCode: z.string(),
    area: z.string(),
    publishedAt: z.string(),
});
export const publishedAreaReportListResponseSchema = z.object({
    reports: z.array(publishedAreaReportSchema),
});
/**
 * The names in every published report, and nothing else — what a wine's page
 * and the producers list match their own names against to link to the report
 * that names them. Task 10.36. One small fetch for all reports, rather than
 * every report's whole content, because the pages ask it of every wine.
 */
export const areaReportIndexEntrySchema = z.object({
    id: z.string(),
    kind: wineTypeSchema,
    countryCode: z.string(),
    area: z.string(),
    regions: z.array(z.string()),
    producers: z.array(z.object({ name: z.string(), cuvees: z.array(z.string()) })),
});
export const areaReportIndexResponseSchema = z.object({
    reports: z.array(areaReportIndexEntrySchema),
});
/**
 * What a fact check can do about a finding — task 10.42, decided 2026-09-29
 * in `project-scope.md`. `replace` swaps one passage for a corrected one,
 * `remove_producer` takes a house out of a region it does not belong in,
 * `set_grade` corrects a vintage, and `note` is a finding with no mechanical
 * fix, shown for the administrator to act on by hand.
 */
export const areaFindingActionSchema = z.enum([
    "replace",
    "remove_producer",
    "set_grade",
    "note",
]);
/** One thing the fact check found wrong, with its source and its fix. */
export const areaFindingSchema = z.object({
    id: z.string(),
    /** Which part of the report it is in: *Området*, a region's name, *Årgångar*. */
    part: z.string(),
    claim: z.string(),
    problem: z.string(),
    action: areaFindingActionSchema,
    find: z.string().nullable(),
    replace: z.string().nullable(),
    producer: z.string().nullable(),
    year: z.number().int().nullable(),
    grade: z.number().int().min(1).max(5).nullable(),
    source: z.object({ title: z.string(), url: z.string() }).nullable(),
    applied: z.boolean(),
    /** Whether the fix can still be made — false for a `note`, or once the
     *  passage it would change is no longer in the report. */
    applicable: z.boolean(),
});
/** A fact check of one report: when, by which model, and what it found. */
export const areaReviewSchema = z.object({
    reviewedAt: z.string(),
    model: z.string(),
    findings: z.array(areaFindingSchema),
    /** How many parts were checked, and how many could not be — a part that
     *  ran out of time is said, not taken for a clean bill. */
    parts: z.number().int(),
    failedParts: z.number().int(),
});
/** The fixes the administrator chose to make. */
export const applyAreaFindingsRequestSchema = z.object({
    ids: z.array(z.string()).min(1),
});
/** One report with its content — published for anybody, any for the
 *  administrator. */
export const areaReportSchema = areaReportSummarySchema.extend({
    content: areaReportContentSchema,
    /** The latest fact check — the administrator's only; null for everybody
     *  else and for a report not yet checked. Required since 2026-09-29, once
     *  revision 68 was serving it: every route that answers with a whole
     *  report sends it, null included. */
    review: areaReviewSchema.nullable(),
    /** The corrections themselves, for the administrator's fact-check panel;
     *  empty for everybody else. Required since 2026-09-29, once revision 70
     *  was serving it. */
    corrections: z.array(areaCorrectionSchema),
});
/**
 * What to describe: a kind, a country and an area inside it.
 *
 * **The area is free text** — the model is better at *Piemonte* than any list
 * this application could keep — and is matched against the areas already run
 * in the same country, so a second spelling lands on the first. Eighty
 * characters is room for *Côtes de Bordeaux Saint-Macaire* and not for a
 * paragraph.
 */
export const runAreaReportRequestSchema = z.object({
    kind: wineTypeSchema,
    // ISO 3166-1 alpha-2, as the wine form stores it.
    countryCode: z
        .string()
        .transform((value) => value.trim().toUpperCase())
        .pipe(z.string().regex(/^[A-Z]{2}$/)),
    area: z.string().trim().min(1).max(80),
});
/**
 * A wish for a report — task 10.33, decided 2026-09-28 in `project-scope.md`.
 *
 * **Anybody may wish, guests included**, since a wish costs nothing; running
 * the report stays the administrator's. A wish is met by a publish of the
 * same kind, country and area, or declined with an optional line of why.
 */
export const areaWishStateSchema = z.enum(["open", "met", "declined"]);
/** One of the reader's own wishes, and what became of it. */
export const myAreaWishSchema = z.object({
    id: z.string(),
    kind: wineTypeSchema,
    countryCode: z.string(),
    area: z.string(),
    why: z.string().nullable(),
    state: areaWishStateSchema,
    /** The administrator's line when it was declined. */
    reason: z.string().nullable(),
    wishedAt: z.string(),
    /** How many other accounts wish for the same, while it is open. Counted,
     *  never named: who else wished is the administrator's to see. */
    others: z.number().int(),
    /** The published report that met it, to link to. */
    reportId: z.string().nullable(),
});
export const myAreaWishListResponseSchema = z.object({
    wishes: z.array(myAreaWishSchema),
});
/**
 * The open wishes for one kind, country and area, as the administrator sees
 * them: gathered into one, because *three people want Rioja* is the fact
 * worth seeing, with who and when and why for each.
 */
export const openAreaWishGroupSchema = z.object({
    kind: wineTypeSchema,
    countryCode: z.string(),
    area: z.string(),
    wishes: z.array(z.object({
        id: z.string(),
        who: z.string(),
        why: z.string().nullable(),
        wishedAt: z.string(),
    })),
});
export const openAreaWishListResponseSchema = z.object({
    groups: z.array(openAreaWishGroupSchema),
});
/** A wish, as it is made. The area is matched as a run's is. */
export const wishAreaReportRequestSchema = runAreaReportRequestSchema.extend({
    why: z
        .string()
        .trim()
        .max(200)
        .transform((value) => (value === "" ? null : value))
        .nullable()
        .default(null),
});
/** Declining every open wish for one kind, country and area at once. */
export const declineAreaWishesRequestSchema = runAreaReportRequestSchema.extend({
    reason: z
        .string()
        .trim()
        .max(200)
        .transform((value) => (value === "" ? null : value))
        .nullable()
        .default(null),
});
//# sourceMappingURL=area-report.js.map